import merge from 'lodash/merge'

interface ColumnProps {
  columnKey: string
}

type ColumnsToCsvMapperProps = Record<string, string | string[]>

interface GenerateCsvExportBodyProps {
  columns: ColumnProps[]
  columnsToCsvMapper: ColumnsToCsvMapperProps
}

const generateObjectFormKey = (currentObjectToMap: string) => {
  const splitString = currentObjectToMap.split('.')
  return splitString.reduceRight((accObj, next) => ({ [next]: accObj }), {})
}

/**
 * @param columnsToCsvMapper - this mapper is used to map the columnKey to the object value on the API
 * The reason we have a mapper is to allow us to get data for columns that columnsKeys
 * don't match the data path or require multiple data points.
 * For usage it follows the following format { [columnKey]: 'dataValue' }
 * There are a few utils that can be used to get data:
 * Nested object values: use '.' for example 'data.nested' will return { data: nested }
 * Multiple Data value: use an array of strings to get all values to be generated by that column
 *   ex: ['data.name', 'data.id', 'newValue'] will return { data: { name, id } newValue }
 * Alias: for alias start a string with the alias and end it with ':' ex: `reallyCoolName: data.nested`
 *   will return { reallyCoolName: { nested } }
 * @returns
 */

export const generateCsvExportBody = ({
  columns,
  columnsToCsvMapper,
}: GenerateCsvExportBodyProps) => {
  const generateExportObject = columns.reduce((accColumnExport, currentColumn) => {
    const currentObjectToMap = columnsToCsvMapper[currentColumn.columnKey]

    if (currentObjectToMap) {
      const columnsToExport = Array.isArray(currentObjectToMap)
        ? currentObjectToMap.reduce(
            (accObjectToMap, objectToMap) =>
              merge(accObjectToMap, generateObjectFormKey(objectToMap)),
            {},
          )
        : generateObjectFormKey(currentObjectToMap)

      return merge(accColumnExport, columnsToExport)
    }

    return accColumnExport
  }, {})

  return JSON.stringify(generateExportObject)
    .replace(/[:,"](?<!_:)/g, '')
    .replaceAll('{}', ' \n ')
}
